import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';
import { toast } from '../components/ui/Toast';
import type { CreateProjectInput, UpdateProjectInput, UpdateEstadoInput } from '@shared/validation/project-schemas';
import type { CreateMaestroAssignmentInput, CreateMaestroProjectRateInput } from '@shared/validation/maestro-schemas';

// ── DTOs ──
export interface ProjectListDTO {
  id: string;
  codigo: string;
  nombre: string;
  estado: string;
  client: { id: string; nombre: string };
  residente: { id: string; nombre: string };
  tiene_gypsum: boolean;
  tiene_pintura: boolean;
  tiene_impermeabilizacion: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjectDetailDTO extends ProjectListDTO {
  direccion_obra: string;
  medidas_notas: string | null;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  client: {
    id: string;
    nombre: string;
    ruc_ci: string | null;
    telefono: string | null;
    email: string | null;
  };
}

export interface ProjectCreateDTO {
  id: string;
  codigo: string;
  nombre: string;
  estado: string;
}

interface ProjectsResponse {
  data: ProjectListDTO[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface ProjectFilters {
  estado?: string;
  search?: string;
  page?: number;
  limit?: number;
}

// ── List ──
export function useProjects(filters?: ProjectFilters) {
  return useQuery({
    queryKey: queryKeys.projects.list(filters as Record<string, unknown>),
    queryFn: async () => {
      const response = await api.get<ProjectsResponse>('/projects', {
        params: filters,
      });
      return response.data;
    },
  });
}

// ── Detail ──
export function useProject(id: string) {
  return useQuery({
    queryKey: queryKeys.projects.detail(id),
    queryFn: async () => {
      const response = await api.get<{ data: ProjectDetailDTO }>(`/projects/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

// ── Create ──
export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateProjectInput) => {
      const response = await api.post<{ data: ProjectCreateDTO }>('/projects', data);
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      toast('success', `Proyecto ${data.codigo} creado exitosamente`);
    },
  });
}

// ── Update ──
export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateProjectInput }) => {
      const response = await api.put<{ data: ProjectCreateDTO }>(`/projects/${id}`, data);
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(variables.id) });
      toast('success', 'Proyecto actualizado');
    },
  });
}

// ── Project Maestros DTOs ──
interface ProjectRateMdoItemDTO {
  material_id: string;
  material_nombre: string;
  precio_unitario: number;
}

interface ProjectRateDTO {
  rate_id: string;
  recipe_id: string;
  recipe_nombre: string;
  precio_unitario: number;
  unidad: string;
  mdo_items: ProjectRateMdoItemDTO[];
}

export interface AssignedMaestroDTO {
  assignment_id: string;
  maestro_id: string;
  nombre: string;
  cedula: string | null;
  especialidades: string[];
  is_active: boolean;
  project_rates: ProjectRateDTO[];
}

// ── List project maestros ──
export function useProjectMaestros(projectId: string) {
  return useQuery({
    queryKey: queryKeys.projectMaestros.list(projectId),
    queryFn: async () => {
      const response = await api.get<{ data: AssignedMaestroDTO[] }>(`/projects/${projectId}/maestros`);
      return response.data.data;
    },
    enabled: !!projectId,
  });
}

// ── Assign maestro ──
export function useAssignMaestro() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, data }: { projectId: string; data: CreateMaestroAssignmentInput }) => {
      const response = await api.post<{ data: { assignment_id: string; maestro_nombre: string } }>(
        `/projects/${projectId}/maestros`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projectMaestros.list(variables.projectId) });
      toast('success', `Maestro "${_data.maestro_nombre}" asignado`);
    },
  });
}

// ── Remove maestro ──
export function useRemoveMaestro() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, maestroId }: { projectId: string; maestroId: string }) => {
      const response = await api.delete<{ data: { removed: boolean; had_planillas: boolean } }>(
        `/projects/${projectId}/maestros/${maestroId}`,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projectMaestros.list(variables.projectId) });
      toast('success', 'Maestro removido del proyecto');
    },
  });
}

// ── Set project rate ──
export function useSetProjectRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      maestroId,
      data,
    }: {
      projectId: string;
      maestroId: string;
      data: CreateMaestroProjectRateInput;
    }) => {
      const response = await api.put<{ data: { rate_id: string; recipe_id: string; recipe_nombre: string; precio_unitario: number; unidad: string } }>(
        `/projects/${projectId}/maestros/${maestroId}/rates`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      // Update cache directly so the rate appears immediately without waiting for a refetch
      queryClient.setQueryData<AssignedMaestroDTO[]>(
        queryKeys.projectMaestros.list(variables.projectId),
        (old) => {
          if (!old) return old;
          return old.map((m) => {
            if (m.maestro_id !== variables.maestroId) return m;
            const newRate: ProjectRateDTO = {
              rate_id: _data.rate_id,
              recipe_id: _data.recipe_id,
              recipe_nombre: _data.recipe_nombre,
              precio_unitario: _data.precio_unitario,
              unidad: _data.unidad,
              mdo_items: [],
            };
            const existingIdx = m.project_rates.findIndex((r) => r.recipe_id === _data.recipe_id);
            const updatedRates = existingIdx >= 0
              ? m.project_rates.map((r, i) => (i === existingIdx ? newRate : r))
              : [...m.project_rates, newRate];
            return { ...m, project_rates: updatedRates };
          });
        },
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.projectMaestros.list(variables.projectId) });
      toast('success', `Tarifa actualizada: $${_data.precio_unitario}`);
    },
  });
}

// ── Delete project rate ──
export function useDeleteProjectRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, maestroId, rateId }: { projectId: string; maestroId: string; rateId: string }) => {
      await api.delete(`/projects/${projectId}/maestros/${maestroId}/rates/${rateId}`);
      return { projectId, maestroId, rateId };
    },
    onSuccess: (_data, variables) => {
      queryClient.setQueryData<AssignedMaestroDTO[]>(
        queryKeys.projectMaestros.list(variables.projectId),
        (old) => {
          if (!old) return old;
          return old.map((m) => {
            if (m.maestro_id !== variables.maestroId) return m;
            return { ...m, project_rates: m.project_rates.filter((r) => r.rate_id !== variables.rateId) };
          });
        },
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.projectMaestros.list(variables.projectId) });
      toast('success', 'Tarifa eliminada');
    },
  });
}

// ── Sync maestro rates from proforma ──
export function useSyncMaestroRates() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId: string) => {
      const response = await api.post<{ data: { seeded: number } }>(
        `/projects/${projectId}/sync-maestro-rates`,
        {},
      );
      return response.data.data;
    },
    onSuccess: (_data, projectId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projectMaestros.list(projectId) });
      toast('success', `APUs sincronizados (${_data.seeded} nuevos)`);
    },
  });
}

// ── Update estado (state transition) ──
export function useUpdateEstado() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateEstadoInput }) => {
      const response = await api.put<{ data: { id: string; estado: string; updated_at: string } }>(
        `/projects/${id}/estado`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.projectSummary.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.summary });
      toast('success', 'Estado del proyecto actualizado');
    },
  });
}

// ── Delete project (hard, initial states only) ──
export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/projects/${id}`);
      return response.data;
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.projects.all });
      // Snapshot all active project list queries for rollback
      const previousQueries = queryClient.getQueriesData<ProjectsResponse>({
        queryKey: queryKeys.projects.all,
      });
      // Immediately remove project from all cached list queries
      queryClient.setQueriesData<ProjectsResponse>(
        { queryKey: queryKeys.projects.all },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.filter((p) => p.id !== id),
            pagination: { ...old.pagination, total: old.pagination.total - 1 },
          };
        },
      );
      return { previousQueries };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.summary });
      toast('success', 'Proyecto eliminado');
    },
    onError: (_err, _id, context) => {
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      // api-client interceptor already shows the specific 409 message
    },
  });
}
